import React, { useState, createContext, useContext, Component } from 'react';
// ─── Theme Token Types ────────────────────────────────────────────────────────
interface Theme {
  pageBg: string;
  sidebarBg: string;
  surface: string;
  card: string;
  deepCard: string;
  inputBg: string;
  green: string;
  greenDim: string;
  greenFaint: string;
  greenGhost: string;
  white: string;
  textSecondary: string;
  textMuted: string;
  textLabel: string;
  border: string;
  borderCard: string;
  warning: string;
  error: string;
}
const DARK_THEME: Theme = {
  pageBg: '#111111',
  sidebarBg: '#0a0a0a',
  surface: '#1a1a1a',
  card: '#1f1f1f',
  deepCard: '#0f0f0f',
  inputBg: '#161616',
  green: '#1A8FE8',
  greenDim: '#1578C5',
  greenFaint: 'rgba(26,143,232,0.06)',
  greenGhost: 'rgba(26,143,232,0.12)',
  white: '#f0f0f0',
  textSecondary: '#5a5a5a',
  textMuted: '#3a3a3a',
  textLabel: '#4a4a4a',
  border: '#252525',
  borderCard: '#1f1f1f',
  warning: '#f0a030',
  error: '#e05252'
};
const LIGHT_THEME: Theme = {
  pageBg: '#f4f4f4',
  sidebarBg: '#ffffff',
  surface: '#eeeeee',
  card: '#e8e8e8',
  deepCard: '#f9f9f9',
  inputBg: '#ffffff',
  green: '#1A8FE8',
  greenDim: '#1578C5',
  greenFaint: 'rgba(26,143,232,0.08)',
  greenGhost: 'rgba(26,143,232,0.15)',
  white: '#1a1a1a',
  textSecondary: '#555555',
  textMuted: '#999999',
  textLabel: '#666666',
  border: '#d4d4d4',
  borderCard: '#e0e0e0',
  warning: '#c47a10',
  error: '#c04040'
};
const ThemeContext = createContext<Theme>(DARK_THEME);
// ─── Step Definitions ─────────────────────────────────────────────────────────
const STEPS = [{
  id: 1,
  label: 'Borrower',
  short: 'Borrower'
}, {
  id: 2,
  label: 'Property',
  short: 'Property'
}, {
  id: 3,
  label: 'Terms',
  short: 'Terms'
}, {
  id: 4,
  label: 'IPAC',
  short: 'IPAC'
}, {
  id: 5,
  label: 'Lender',
  short: 'Lender'
}];
// ─── Form State ───────────────────────────────────────────────────────────────
interface FormData {
  loName: string;
  loNmls: string;
  loEmail: string;
  loPhone: string;
  loSpecialist: string;
  loBranch: string;
  borrowerFirst: string;
  borrowerLast: string;
  coBorrowerFirst: string;
  coBorrowerLast: string;
  borrowerDob: string;
  propAddress: string;
  propCity: string;
  propState: string;
  propZip: string;
  propType: string;
  occupancy: string;
  appraisedValue: string;
  salesPrice: string;
  loanAmount: string;
  loanType: string;
  loanPurpose: string;
  interestRate: string;
  loanTerm: string;
  ltv: string;
  cltv: string;
  dti: string;
  downPayment: string;
  closingDate: string;
  escrowCompany: string;
  lockExpiration: string;
  lenderName: string;
  productName: string;
  ausFinding: string;
  specialInstructions: string;
  submissionNotes: string;
  // IPAC Fields
  ipacSsaIncome: boolean;
  ipacW2Income: boolean;
  ipacSelfEmployed: boolean;
  ipacOtherIncomeType: string;
  ipacFundsToClose: string;
  ipacNoCashFromBorrower: boolean;
  ipacCashFromBorrower: boolean;
  ipacCashToBorrower: string;
  ipacSkippedPayments: string;
  ipacOwnsAdditionalProperties: boolean;
  ipacHasHoaDues: boolean;
  ipacAdditionalLiens: boolean;
  ipacLienType: string;
  ipacLienLender: string;
  ipacSubordinationNeeded: boolean;
  ipacLienToSubordinate: string;
  ipacSubordinatingLender: string;
  ipacAppraisalTransfer: boolean;
  ipacOriginalLenderName: string;
  ipacLenderContactInfo: string;
  ipacAusDu: boolean;
  ipacAusLp: boolean;
  ipacAusManual: boolean;
  ipacAusNa: boolean;
  ipacLeakyRoof: boolean;
  ipacHandrails: boolean;
  ipacToiletsSinks: boolean;
  ipacBrokenWindows: boolean;
  ipacLargeCracks: boolean;
  ipacChippedPaint: boolean;
  ipacConditionExplanation: string;
  ipacAppraisalType: string;
  ipacPurchaseOrRefi: string;
  ipacDownPaymentSource: string;
  ipacCardName: string;
  ipacCardNumber: string;
  ipacCardType: string;
  ipacCardExp: string;
  ipacCardCvv: string;
  ipacCurrentTitleHolders: string;
  ipacVestingAfterClosing: string;
  ipacRemoveFromTitle: boolean;
  ipacNamesToRemove: string;
  ipacAddAtClosing: boolean;
  ipacNamesToAdd: string;
}
const EMPTY_FORM: FormData = {
  loName: '',
  loNmls: '',
  loEmail: '',
  loPhone: '',
  loSpecialist: '',
  loBranch: '',
  borrowerFirst: '',
  borrowerLast: '',
  coBorrowerFirst: '',
  coBorrowerLast: '',
  borrowerDob: '',
  propAddress: '',
  propCity: '',
  propState: '',
  propZip: '',
  propType: '',
  occupancy: '',
  appraisedValue: '',
  salesPrice: '',
  loanAmount: '',
  loanType: '',
  loanPurpose: '',
  interestRate: '',
  loanTerm: '',
  ltv: '',
  cltv: '',
  dti: '',
  downPayment: '',
  closingDate: '',
  escrowCompany: '',
  lockExpiration: '',
  lenderName: '',
  productName: '',
  ausFinding: '',
  specialInstructions: '',
  submissionNotes: '',
  ipacSsaIncome: false,
  ipacW2Income: false,
  ipacSelfEmployed: false,
  ipacOtherIncomeType: '',
  ipacFundsToClose: '',
  ipacNoCashFromBorrower: false,
  ipacCashFromBorrower: false,
  ipacCashToBorrower: '',
  ipacSkippedPayments: '',
  ipacOwnsAdditionalProperties: false,
  ipacHasHoaDues: false,
  ipacAdditionalLiens: false,
  ipacLienType: '',
  ipacLienLender: '',
  ipacSubordinationNeeded: false,
  ipacLienToSubordinate: '',
  ipacSubordinatingLender: '',
  ipacAppraisalTransfer: false,
  ipacOriginalLenderName: '',
  ipacLenderContactInfo: '',
  ipacAusDu: false,
  ipacAusLp: false,
  ipacAusManual: false,
  ipacAusNa: false,
  ipacLeakyRoof: false,
  ipacHandrails: false,
  ipacToiletsSinks: false,
  ipacBrokenWindows: false,
  ipacLargeCracks: false,
  ipacChippedPaint: false,
  ipacConditionExplanation: '',
  ipacAppraisalType: '',
  ipacPurchaseOrRefi: '',
  ipacDownPaymentSource: '',
  ipacCardName: '',
  ipacCardNumber: '',
  ipacCardType: '',
  ipacCardExp: '',
  ipacCardCvv: '',
  ipacCurrentTitleHolders: '',
  ipacVestingAfterClosing: '',
  ipacRemoveFromTitle: false,
  ipacNamesToRemove: '',
  ipacAddAtClosing: false,
  ipacNamesToAdd: ''
};
// ─── Shared Field Components ──────────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  required = false,
  prefix = '',
  suffix = '',
  hint = '',
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  prefix?: string;
  suffix?: string;
  hint?: string;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-1">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-2">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-3">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    }} data-id="element-4">
        {prefix && <span style={{
        position: 'absolute',
        left: '14px',
        color: c.textSecondary,
        fontSize: '14px',
        pointerEvents: 'none',
        zIndex: 1
      }} data-id="element-5">
            {prefix}
          </span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: `13px ${suffix ? '42px' : '16px'} 13px ${prefix ? '32px' : '16px'}`,
        fontSize: '14px',
        color: c.white,
        outline: 'none',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box',
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-6" />
        {suffix && <span style={{
        position: 'absolute',
        right: '14px',
        color: c.textSecondary,
        fontSize: '12px',
        pointerEvents: 'none',
        fontWeight: 600,
        letterSpacing: '0.5px'
      }} data-id="element-7">
            {suffix}
          </span>}
      </div>
      {hint && <span style={{
      fontSize: '10px',
      color: c.textMuted,
      letterSpacing: '0.5px'
    }} data-id="element-8">
          {hint}
        </span>}
    </div>;
}
function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  required?: boolean;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-9">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-10">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-11">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative'
    }} data-id="element-12">
        <select value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: '13px 40px 13px 16px',
        fontSize: '14px',
        color: value ? c.white : c.textSecondary,
        outline: 'none',
        appearance: 'none',
        cursor: 'pointer',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box'
      }} data-id="element-13">
          <option value="" style={{
          color: c.textSecondary
        }} data-id="element-14">
            Select…
          </option>
          {options.map(o => <option key={o.value} value={o.value} style={{
          backgroundColor: c.inputBg,
          color: c.white
        }} data-id="element-15">
              {o.label}
            </option>)}
        </select>
        <span style={{
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: c.textSecondary,
        fontSize: '9px',
        pointerEvents: 'none',
        fontWeight: 700
      }} data-id="element-16">
          ▼
        </span>
      </div>
    </div>;
}
function Textarea({
  label,
  value,
  onChange,
  placeholder = '',
  rows = 4,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-17">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-18">
        {label}
      </label>
      <textarea value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} rows={rows} style={{
      width: '100%',
      backgroundColor: c.inputBg,
      border: `1px solid ${focused ? c.green : c.border}`,
      borderRadius: '6px',
      padding: '13px 16px',
      fontSize: '14px',
      color: c.white,
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxSizing: 'border-box'
    }} data-id="element-19" />
    </div>;
}
function Checkbox({
  label,
  checked,
  onChange,
  fullWidth = false
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <label style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    gridColumn: fullWidth ? '1 / -1' : undefined,
    userSelect: 'none'
  }} data-id="element-20">
      <div style={{
      width: '18px',
      height: '18px',
      borderRadius: '4px',
      border: `1px solid ${checked ? c.green : c.border}`,
      backgroundColor: checked ? c.green : c.inputBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
      flexShrink: 0
    }} data-id="element-21">
        {checked && <span style={{
        color: '#0a0a0a',
        fontSize: '12px',
        fontWeight: 900
      }} data-id="element-22">
            ✓
          </span>}
      </div>
      <span style={{
      fontSize: '13px',
      color: c.white
    }} data-id="element-23">
        {label}
      </span>
    </label>;
}
function YesNoToggle({
  label,
  value,
  onChange,
  fullWidth = false
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-24">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: c.textLabel
    }} data-id="element-25">
        {label}
      </label>
      <div style={{
      display: 'flex',
      gap: '8px'
    }} data-id="element-26">
        <button onClick={() => onChange(true)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === true ? c.green : c.border}`,
        backgroundColor: value === true ? c.greenFaint : c.inputBg,
        color: value === true ? c.green : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-27">
          Yes
        </button>
        <button onClick={() => onChange(false)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === false ? c.warning : c.border}`,
        backgroundColor: value === false ? `${c.warning}15` : c.inputBg,
        color: value === false ? c.warning : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-28">
          No
        </button>
      </div>
    </div>;
}
function SectionDivider({
  label
}: {
  label: string;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '36px 0 24px'
  }} data-id="element-29">
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-30" />
      <span style={{
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: c.green
    }} data-id="element-31">
        {label}
      </span>
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-32" />
    </div>;
}
function FieldSection({
  children
}: {
  children: React.ReactNode;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    borderLeft: `3px solid ${c.green}`,
    paddingLeft: '28px'
  }} data-id="element-33">
      {children}
    </div>;
}
// ─── Step Content Components ──────────────────────────────────────────────────
function StepLoanOfficer({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-34">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-35">
        <Field label="Full Name" value={form.loName} onChange={v => set('loName', v)} required placeholder="Jane Smith" data-id="element-36" />
        <Field label="NMLS ID" value={form.loNmls} onChange={v => set('loNmls', v)} required placeholder="1234567" data-id="element-37" />
        <Field label="Email Address" value={form.loEmail} onChange={v => set('loEmail', v)} type="email" required placeholder="jane@ezloansmi.com" data-id="element-38" />
        <Field label="Phone Number" value={form.loPhone} onChange={v => set('loPhone', v)} type="tel" placeholder="(844) 204-7863" data-id="element-39" />
        <Field label="Branch" value={form.loBranch} onChange={v => set('loBranch', v)} placeholder="Troy, MI" data-id="element-40" />
        <Select label="Submission Specialist" value={form.loSpecialist} onChange={v => set('loSpecialist', v)} required options={[{
        value: 'sarah',
        label: 'Sarah Johnson'
      }, {
        value: 'mike',
        label: 'Mike Chen'
      }, {
        value: 'lisa',
        label: 'Lisa Rodriguez'
      }, {
        value: 'david',
        label: 'David Kim'
      }, {
        value: 'amanda',
        label: 'Amanda Torres'
      }]} data-id="element-41" />
      </div>
    </FieldSection>;
}
function StepBorrower({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-42">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-43">
        <Field label="First Name" value={form.borrowerFirst} onChange={v => set('borrowerFirst', v)} required placeholder="John" data-id="element-44" />
        <Field label="Last Name" value={form.borrowerLast} onChange={v => set('borrowerLast', v)} required placeholder="Doe" data-id="element-45" />
        <Field label="Date of Birth" value={form.borrowerDob} onChange={v => set('borrowerDob', v)} type="date" required data-id="element-46" />
      </div>
      <SectionDivider label="Co-Borrower" data-id="element-47" />
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-48">
        <Field label="First Name" value={form.coBorrowerFirst} onChange={v => set('coBorrowerFirst', v)} placeholder="Jane" data-id="element-49" />
        <Field label="Last Name" value={form.coBorrowerLast} onChange={v => set('coBorrowerLast', v)} placeholder="Doe" data-id="element-50" />
      </div>
    </FieldSection>;
}
function StepProperty({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  return <FieldSection data-id="element-51">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-52">
        <Field label="Street Address" value={form.propAddress} onChange={v => set('propAddress', v)} required placeholder="123 Main Street" fullWidth data-id="element-53" />
        <Field label="City" value={form.propCity} onChange={v => set('propCity', v)} required placeholder="Troy" data-id="element-54" />
        <Select label="State" value={form.propState} onChange={v => set('propState', v)} required options={states.map(s => ({
        value: s,
        label: s
      }))} data-id="element-55" />
        <Field label="ZIP Code" value={form.propZip} onChange={v => set('propZip', v)} required placeholder="48083" data-id="element-56" />
        <Select label="Property Type" value={form.propType} onChange={v => set('propType', v)} required options={[{
        value: 'sfr',
        label: 'Single Family Residence'
      }, {
        value: 'condo',
        label: 'Condominium'
      }, {
        value: 'townhome',
        label: 'Townhome / PUD'
      }, {
        value: '2unit',
        label: '2-Unit'
      }, {
        value: '3unit',
        label: '3-Unit'
      }, {
        value: '4unit',
        label: '4-Unit'
      }, {
        value: 'mfh',
        label: 'Manufactured Home'
      }]} data-id="element-57" />
        <Select label="Occupancy" value={form.occupancy} onChange={v => set('occupancy', v)} required options={[{
        value: 'primary',
        label: 'Primary Residence'
      }, {
        value: 'second',
        label: 'Second Home'
      }, {
        value: 'investment',
        label: 'Investment Property'
      }]} data-id="element-58" />
        <Field label="Appraised Value" value={form.appraisedValue} onChange={v => set('appraisedValue', v)} required prefix="$" placeholder="350,000" data-id="element-59" />
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-60" />
      </div>
    </FieldSection>;
}
function StepLoanTerms({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-61">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-62">
        <Field label="Loan Amount" value={form.loanAmount} onChange={v => set('loanAmount', v)} required prefix="$" placeholder="310,000" data-id="element-63" />
        <Select label="Loan Type" value={form.loanType} onChange={v => set('loanType', v)} required options={[{
        value: 'conv',
        label: 'Conventional'
      }, {
        value: 'fha',
        label: 'FHA'
      }, {
        value: 'va',
        label: 'VA'
      }, {
        value: 'usda',
        label: 'USDA'
      }, {
        value: 'jumbo',
        label: 'Jumbo'
      }, {
        value: 'nonqm',
        label: 'Non-QM'
      }]} data-id="element-64" />
        <Select label="Loan Purpose" value={form.loanPurpose} onChange={v => set('loanPurpose', v)} required options={[{
        value: 'purchase',
        label: 'Purchase'
      }, {
        value: 'rr',
        label: 'Rate & Term Refinance'
      }, {
        value: 'cashout',
        label: 'Cash-Out Refinance'
      }, {
        value: 'streamline',
        label: 'Streamline Refinance'
      }]} data-id="element-65" />
        <Select label="Loan Term" value={form.loanTerm} onChange={v => set('loanTerm', v)} required options={[{
        value: '360',
        label: '30-Year Fixed'
      }, {
        value: '240',
        label: '20-Year Fixed'
      }, {
        value: '180',
        label: '15-Year Fixed'
      }, {
        value: '120',
        label: '10-Year Fixed'
      }, {
        value: '5_1arm',
        label: '5/1 ARM'
      }, {
        value: '7_1arm',
        label: '7/1 ARM'
      }, {
        value: '10_1arm',
        label: '10/1 ARM'
      }]} data-id="element-66" />
        <Field label="Interest Rate" value={form.interestRate} onChange={v => set('interestRate', v)} required suffix="%" placeholder="6.875" hint="Note rate, not APR" data-id="element-67" />
        <Field label="LTV" value={form.ltv} onChange={v => set('ltv', v)} required suffix="%" placeholder="88.57" data-id="element-68" />
        <Field label="CLTV" value={form.cltv} onChange={v => set('cltv', v)} suffix="%" placeholder="88.57" data-id="element-69" />
        <Field label="Back-End DTI" value={form.dti} onChange={v => set('dti', v)} required suffix="%" placeholder="43.5" data-id="element-70" />
      </div>
    </FieldSection>;
}
function StepIPAC({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <div data-id="element-71">
      <SectionDivider label="Section 1 — Borrower Income" data-id="element-72" />
      <FieldSection data-id="element-73">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-74">
          <Checkbox label="SSA / Disability Income" checked={form.ipacSsaIncome} onChange={v => set('ipacSsaIncome', v)} data-id="element-75" />
          <Checkbox label="W-2 Income" checked={form.ipacW2Income} onChange={v => set('ipacW2Income', v)} data-id="element-76" />
          <Checkbox label="Self-Employed" checked={form.ipacSelfEmployed} onChange={v => set('ipacSelfEmployed', v)} data-id="element-77" />
          <Field label="Other Income Type" value={form.ipacOtherIncomeType} onChange={v => set('ipacOtherIncomeType', v)} placeholder="e.g. Rental, Pension" data-id="element-78" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 2 — Funds to Close" data-id="element-79" />
      <FieldSection data-id="element-80">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-81">
          <Field label="Funds to Close" value={form.ipacFundsToClose} onChange={v => set('ipacFundsToClose', v)} prefix="$" placeholder="0.00" data-id="element-82" />
          <Field label="Cash to Borrower" value={form.ipacCashToBorrower} onChange={v => set('ipacCashToBorrower', v)} prefix="$" placeholder="0.00" data-id="element-83" />
          <Checkbox label="No Cash from Borrower" checked={form.ipacNoCashFromBorrower} onChange={v => set('ipacNoCashFromBorrower', v)} data-id="element-84" />
          <Checkbox label="Cash from Borrower" checked={form.ipacCashFromBorrower} onChange={v => set('ipacCashFromBorrower', v)} data-id="element-85" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 3 — Payment Skip" data-id="element-86" />
      <FieldSection data-id="element-87">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-88">
          <Select label="Skipped Payments" value={form.ipacSkippedPayments} onChange={v => set('ipacSkippedPayments', v)} options={[{
          value: 'none',
          label: 'None'
        }, {
          value: '1',
          label: '1 Payment'
        }, {
          value: '2',
          label: '2 Payments'
        }]} data-id="element-89" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 4 — Property Details" data-id="element-90" />
      <FieldSection data-id="element-91">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-92">
          <YesNoToggle label="Borrower Owns Additional Properties" value={form.ipacOwnsAdditionalProperties} onChange={v => set('ipacOwnsAdditionalProperties', v)} data-id="element-93" />
          <YesNoToggle label="Property Has HOA Dues" value={form.ipacHasHoaDues} onChange={v => set('ipacHasHoaDues', v)} data-id="element-94" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 5 — Existing Liens" data-id="element-95" />
      <FieldSection data-id="element-96">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-97">
          <YesNoToggle label="Additional Liens on Property" value={form.ipacAdditionalLiens} onChange={v => set('ipacAdditionalLiens', v)} fullWidth data-id="element-98" />
          {form.ipacAdditionalLiens && <>
              <Select label="Lien Type" value={form.ipacLienType} onChange={v => set('ipacLienType', v)} options={[{
            value: 'solar',
            label: 'Solar'
          }, {
            value: 'heloc',
            label: 'HELOC'
          }, {
            value: 'purchase_money',
            label: 'Purchase Money Second'
          }, {
            value: 'other',
            label: 'Other'
          }]} data-id="element-99" />
              <Field label="Lien Lender" value={form.ipacLienLender} onChange={v => set('ipacLienLender', v)} placeholder="e.g. Sunrun" data-id="element-100" />
            </>}
        </div>
      </FieldSection>

      <SectionDivider label="Section 6 — Subordination" data-id="element-101" />
      <FieldSection data-id="element-102">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-103">
          <YesNoToggle label="Subordination Needed" value={form.ipacSubordinationNeeded} onChange={v => set('ipacSubordinationNeeded', v)} fullWidth data-id="element-104" />
          {form.ipacSubordinationNeeded && <>
              <Field label="Lien to Subordinate" value={form.ipacLienToSubordinate} onChange={v => set('ipacLienToSubordinate', v)} placeholder="e.g. HELOC" data-id="element-105" />
              <Field label="Subordinating Lender" value={form.ipacSubordinatingLender} onChange={v => set('ipacSubordinatingLender', v)} placeholder="e.g. Chase Bank" data-id="element-106" />
            </>}
        </div>
      </FieldSection>

      <SectionDivider label="Section 7 — Appraisal Transfer" data-id="element-107" />
      <FieldSection data-id="element-108">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-109">
          <YesNoToggle label="Appraisal Transfer Required" value={form.ipacAppraisalTransfer} onChange={v => set('ipacAppraisalTransfer', v)} fullWidth data-id="element-110" />
          {form.ipacAppraisalTransfer && <>
              <Field label="Original Lender Name" value={form.ipacOriginalLenderName} onChange={v => set('ipacOriginalLenderName', v)} placeholder="e.g. Rocket Mortgage" data-id="element-111" />
              <Field label="Lender Contact Info" value={form.ipacLenderContactInfo} onChange={v => set('ipacLenderContactInfo', v)} placeholder="Email or Phone" data-id="element-112" />
            </>}
        </div>
      </FieldSection>

      <SectionDivider label="Section 8 — Automated Underwriting" data-id="element-113" />
      <FieldSection data-id="element-114">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-115">
          <Checkbox label="DU (Desktop Underwriter)" checked={form.ipacAusDu} onChange={v => set('ipacAusDu', v)} data-id="element-116" />
          <Checkbox label="LP (Loan Product Advisor)" checked={form.ipacAusLp} onChange={v => set('ipacAusLp', v)} data-id="element-117" />
          <Checkbox label="Manual UW" checked={form.ipacAusManual} onChange={v => set('ipacAusManual', v)} data-id="element-118" />
          <Checkbox label="N/A" checked={form.ipacAusNa} onChange={v => set('ipacAusNa', v)} data-id="element-119" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 9 — Property Condition Checklist" data-id="element-120" />
      <FieldSection data-id="element-121">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-122">
          <YesNoToggle label="Leaky Roof or Plumbing" value={form.ipacLeakyRoof} onChange={v => set('ipacLeakyRoof', v)} data-id="element-123" />
          <YesNoToggle label="Handrails on All Stairways" value={form.ipacHandrails} onChange={v => set('ipacHandrails', v)} data-id="element-124" />
          <YesNoToggle label="All Toilets and Sinks Work" value={form.ipacToiletsSinks} onChange={v => set('ipacToiletsSinks', v)} data-id="element-125" />
          <YesNoToggle label="Broken Windows" value={form.ipacBrokenWindows} onChange={v => set('ipacBrokenWindows', v)} data-id="element-126" />
          <YesNoToggle label="Large Cracks in Cement" value={form.ipacLargeCracks} onChange={v => set('ipacLargeCracks', v)} data-id="element-127" />
          <YesNoToggle label="Chipped or Peeling Paint" value={form.ipacChippedPaint} onChange={v => set('ipacChippedPaint', v)} data-id="element-128" />
          <Textarea label="Condition Explanation" value={form.ipacConditionExplanation} onChange={v => set('ipacConditionExplanation', v)} placeholder="Explain any 'Yes' answers above..." fullWidth data-id="element-129" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 10 — Appraisal Handling" data-id="element-130" />
      <FieldSection data-id="element-131">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-132">
          <Select label="Appraisal Type" value={form.ipacAppraisalType} onChange={v => set('ipacAppraisalType', v)} fullWidth options={[{
          value: 'transferred',
          label: 'Transferred Appraisal'
        }, {
          value: 'piw',
          label: 'PIW (Property Inspection Waiver)'
        }, {
          value: 'new',
          label: 'Order New Appraisal'
        }]} data-id="element-133" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 11 — Down Payment / Asset Source" data-id="element-134" />
      <FieldSection data-id="element-135">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-136">
          <Select label="Loan Type" value={form.ipacPurchaseOrRefi} onChange={v => set('ipacPurchaseOrRefi', v)} options={[{
          value: 'purchase',
          label: 'Purchase'
        }, {
          value: 'refinance',
          label: 'Refinance'
        }]} data-id="element-137" />
          {form.ipacPurchaseOrRefi === 'purchase' && <Select label="Source of Down Payment" value={form.ipacDownPaymentSource} onChange={v => set('ipacDownPaymentSource', v)} options={[{
          value: 'personal',
          label: 'Personal Bank Account'
        }, {
          value: 'retirement',
          label: 'Retirement Account'
        }, {
          value: 'transfer',
          label: 'Transfer'
        }, {
          value: 'gift',
          label: 'Gift'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-138" />}
        </div>
      </FieldSection>

      <SectionDivider label="Section 12 — Credit Card Information" data-id="element-139" />
      <FieldSection data-id="element-140">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-141">
          <Field label="Name on Card" value={form.ipacCardName} onChange={v => set('ipacCardName', v)} placeholder="John Doe" data-id="element-142" />
          <Field label="Card Number" value={form.ipacCardNumber} onChange={v => set('ipacCardNumber', v)} placeholder="**** **** **** ****" data-id="element-143" />
          <Select label="Card Type" value={form.ipacCardType} onChange={v => set('ipacCardType', v)} options={[{
          value: 'visa',
          label: 'Visa'
        }, {
          value: 'mastercard',
          label: 'Mastercard'
        }, {
          value: 'amex',
          label: 'American Express'
        }, {
          value: 'discover',
          label: 'Discover'
        }]} data-id="element-144" />
          <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px'
        }} data-id="element-145">
            <Field label="Expiration" value={form.ipacCardExp} onChange={v => set('ipacCardExp', v)} placeholder="MM/YY" data-id="element-146" />
            <Field label="CVV" value={form.ipacCardCvv} onChange={v => set('ipacCardCvv', v)} placeholder="123" data-id="element-147" />
          </div>
        </div>
      </FieldSection>

      <SectionDivider label="Section 13 — Title & Vesting" data-id="element-148" />
      <FieldSection data-id="element-149">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-150">
          <Field label="Current Title Holders" value={form.ipacCurrentTitleHolders} onChange={v => set('ipacCurrentTitleHolders', v)} placeholder="John Doe, Jane Doe" data-id="element-151" />
          <Select label="Vesting After Closing" value={form.ipacVestingAfterClosing} onChange={v => set('ipacVestingAfterClosing', v)} options={[{
          value: 'sole',
          label: 'Sole Owner'
        }, {
          value: 'joint',
          label: 'Joint Tenants'
        }, {
          value: 'common',
          label: 'Tenants in Common'
        }, {
          value: 'community',
          label: 'Community Property'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-152" />
        </div>
      </FieldSection>

      <SectionDivider label="Section 14 — Title Changes" data-id="element-153" />
      <FieldSection data-id="element-154">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }} data-id="element-155">
          <YesNoToggle label="Remove Someone From Title" value={form.ipacRemoveFromTitle} onChange={v => set('ipacRemoveFromTitle', v)} data-id="element-156" />
          {form.ipacRemoveFromTitle && <Field label="Name(s) to Remove" value={form.ipacNamesToRemove} onChange={v => set('ipacNamesToRemove', v)} placeholder="Jane Doe" data-id="element-157" />}

          <YesNoToggle label="Add Someone at Closing" value={form.ipacAddAtClosing} onChange={v => set('ipacAddAtClosing', v)} data-id="element-158" />
          {form.ipacAddAtClosing && <Field label="Name(s) to Add" value={form.ipacNamesToAdd} onChange={v => set('ipacNamesToAdd', v)} placeholder="John Smith" data-id="element-159" />}
        </div>
      </FieldSection>
    </div>;
}
function StepTransaction({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-160">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-161">
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-162" />
        <Field label="Down Payment" value={form.downPayment} onChange={v => set('downPayment', v)} required prefix="$" placeholder="35,000" data-id="element-163" />
        <Field label="Target Closing Date" value={form.closingDate} onChange={v => set('closingDate', v)} type="date" required data-id="element-164" />
        <Field label="Lock Expiration Date" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-165" />
        <Field label="Escrow / Title Company" value={form.escrowCompany} onChange={v => set('escrowCompany', v)} placeholder="First American Title" fullWidth data-id="element-166" />
      </div>
    </FieldSection>;
}
function StepLender({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-167">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-168">
        <Field label="Lender / Investor" value={form.lenderName} onChange={v => set('lenderName', v)} required placeholder="United Wholesale Mortgage" data-id="element-169" />
        <Field label="Product / Program" value={form.productName} onChange={v => set('productName', v)} required placeholder="Conv 30yr Fixed" data-id="element-170" />
        <Select label="AUS Finding" value={form.ausFinding} onChange={v => set('ausFinding', v)} required options={[{
        value: 'approve_eligible',
        label: 'Approve / Eligible (DU)'
      }, {
        value: 'accept',
        label: 'Accept (LP)'
      }, {
        value: 'refer_eligible',
        label: 'Refer / Eligible'
      }, {
        value: 'refer_caution',
        label: 'Refer with Caution'
      }, {
        value: 'manual',
        label: 'Manual Underwrite'
      }]} data-id="element-171" />
        <Field label="Rate Lock Expiration" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-172" />
        <Textarea label="Special Instructions" value={form.specialInstructions} onChange={v => set('specialInstructions', v)} placeholder="Rush request, lender-specific notes, exceptions…" rows={3} fullWidth data-id="element-173" />
        <Textarea label="Submission Notes" value={form.submissionNotes} onChange={v => set('submissionNotes', v)} placeholder="Notes for the submission specialist…" rows={3} fullWidth data-id="element-174" />
      </div>
    </FieldSection>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function LoanSubmissionForm() {
  const [isDark, setIsDark] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const c = isDark ? DARK_THEME : LIGHT_THEME;
  const setField = (key: keyof FormData, value: any) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const handleSubmit = () => {
    setSubmitted(true);
  };
  const handleNext = () => {
    if (step < 5) {
      setCompletedSteps(prev => new Set([...prev, step]));
      setStep(s => s + 1);
    }
  };
  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };
  const goToStep = (n: number) => {
    if (n <= step || completedSteps.has(n - 1)) {
      if (step < n) setCompletedSteps(prev => new Set([...prev, step]));
      setStep(n);
    }
  };
  const stepTitles: Record<number, {
    title: string;
    subtitle: string;
  }> = {
    1: {
      title: 'Borrower',
      subtitle: 'Primary and co-borrower details'
    },
    2: {
      title: 'Property',
      subtitle: 'Subject property details'
    },
    3: {
      title: 'Loan Terms',
      subtitle: 'Core loan parameters and ratios'
    },
    4: {
      title: 'IPAC',
      subtitle: 'Loan File Verification'
    },
    5: {
      title: 'Lender',
      subtitle: 'Product selection, transaction details and notes'
    }
  };
  const padStep = (n: number) => String(n).padStart(2, '0');
  return <ThemeContext.Provider value={c} data-id="element-175">
      <div style={{
      display: 'flex',
      height: '100vh',
      backgroundColor: c.pageBg,
      fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
      overflow: 'hidden',
      transition: 'background-color 0.2s'
    }} data-id="element-176">
        {/* ── SIDEBAR ────────────────────────────────────────────────────── */}
        <aside style={{
        width: '268px',
        flexShrink: 0,
        backgroundColor: c.sidebarBg,
        borderRight: `1px solid ${c.border}`,
        borderLeft: `5px solid ${c.green}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '36px 0',
        overflowY: 'auto',
        transition: 'background-color 0.2s'
      }} data-id="element-177">
          {/* Logo */}
          <div style={{
          padding: '0 28px 24px',
          borderBottom: `1px solid ${c.border}`
        }} data-id="element-178">
            <img src="https://cdn.magicpatterns.com/uploads/5eWChBo5cdoaqwAgAonezW/2246b9849a45a841ad455c43908851563c99a0fa.png" alt="Mortgage One Funding" style={{
            width: '140px',
            height: 'auto',
            filter: isDark ? 'brightness(1.1)' : 'none',
            transition: 'filter 0.2s'
          }} data-id="element-179" />
            <p style={{
            fontSize: '11px',
            color: c.textLabel,
            marginTop: '10px',
            letterSpacing: '0.3px'
          }} data-id="element-180">
              Loan Submission System
            </p>
          </div>

          {/* Theme Toggle */}
          <div style={{
          padding: '16px 28px',
          borderBottom: `1px solid ${c.border}`
        }} data-id="element-181">
            <button onClick={() => setIsDark(d => !d)} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '8px 14px',
            borderRadius: '20px',
            border: `1px solid ${c.border}`,
            backgroundColor: c.deepCard,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '11px',
            fontWeight: 600,
            color: c.textSecondary,
            letterSpacing: '0.3px'
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = c.green;
            e.currentTarget.style.color = c.green;
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = c.border;
            e.currentTarget.style.color = c.textSecondary;
          }} data-id="element-182">
              <span style={{
              fontSize: '13px'
            }} data-id="element-183">
                {isDark ? '☀️' : '🌙'}
              </span>
              <span data-id="element-184">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
          </div>

          {/* Step Navigator */}
          <nav style={{
          flex: 1,
          padding: '20px 0'
        }} data-id="element-185">
            {STEPS.map(s => {
            const isActive = step === s.id;
            const isDone = completedSteps.has(s.id);
            const isLocked = s.id > step && !completedSteps.has(s.id);
            return <button key={s.id} onClick={() => !isLocked && goToStep(s.id)} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              width: '100%',
              padding: '13px 28px',
              border: 'none',
              borderLeft: `3px solid ${isActive ? c.green : 'transparent'}`,
              backgroundColor: isActive ? c.greenFaint : 'transparent',
              cursor: isLocked ? 'default' : 'pointer',
              transition: 'all 0.15s',
              textAlign: 'left',
              position: 'relative',
              overflow: 'hidden'
            }} data-id="element-186">
                  <span style={{
                position: 'absolute',
                right: '-8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '52px',
                fontWeight: 900,
                color: isActive ? c.greenGhost : isDark ? '#1a1a1a' : '#e8e8e8',
                pointerEvents: 'none',
                userSelect: 'none',
                zIndex: 0,
                lineHeight: 1,
                letterSpacing: '-2px',
                transition: 'color 0.2s'
              }} data-id="element-187">
                    {padStep(s.id)}
                  </span>
                  <div style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 800,
                backgroundColor: isDone ? c.green : 'transparent',
                border: `2px solid ${isDone ? c.green : isActive ? c.green : c.textMuted}`,
                color: isDone ? isDark ? '#0a0a0a' : '#ffffff' : isActive ? c.green : c.textMuted,
                transition: 'all 0.2s',
                zIndex: 1,
                position: 'relative'
              }} data-id="element-188">
                    {isDone ? '✓' : padStep(s.id)}
                  </div>
                  <div style={{
                zIndex: 1,
                position: 'relative'
              }} data-id="element-189">
                    <p style={{
                  margin: 0,
                  fontSize: '13px',
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? c.white : isDone ? c.textSecondary : c.textMuted,
                  letterSpacing: isActive ? '0.2px' : '0',
                  transition: 'color 0.15s'
                }} data-id="element-190">
                      {s.label}
                    </p>
                    {isActive && <p style={{
                  margin: '2px 0 0',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: c.green,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }} data-id="element-191">
                        In Progress
                      </p>}
                  </div>
                </button>;
          })}
          </nav>
        </aside>

        {/* ── CONTENT AREA ───────────────────────────────────────────────── */}
        <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: c.pageBg,
        transition: 'background-color 0.2s'
      }} data-id="element-192">
          {/* Step Header */}
          <div style={{
          padding: '32px 56px 28px',
          borderBottom: `1px solid ${c.border}`,
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }} data-id="element-193">
            <span style={{
            position: 'absolute',
            right: '48px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '140px',
            fontWeight: 900,
            color: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.04)',
            letterSpacing: '-8px',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            lineHeight: 1
          }} data-id="element-194">
              {padStep(step)}
            </span>
            <div style={{
            position: 'relative',
            zIndex: 1,
            maxWidth: '860px',
            margin: '0 auto'
          }} data-id="element-195">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '10px'
            }} data-id="element-196">
                <span style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                color: c.textLabel
              }} data-id="element-197">
                  Step {padStep(step)} / {padStep(STEPS.length)}
                </span>
                <div style={{
                display: 'flex',
                gap: '3px',
                alignItems: 'center'
              }} data-id="element-198">
                  {STEPS.map(s => <div key={s.id} style={{
                  width: s.id === step ? '16px' : '4px',
                  height: '4px',
                  borderRadius: '1px',
                  backgroundColor: completedSteps.has(s.id) ? c.green : s.id === step ? c.green : c.border,
                  transition: 'all 0.3s ease'
                }} data-id="element-199" />)}
                </div>
              </div>
              <h1 style={{
              margin: 0,
              fontSize: '40px',
              fontWeight: 800,
              color: c.white,
              letterSpacing: '-1.5px',
              lineHeight: 1.1
            }} data-id="element-200">
                {stepTitles[step].title}
              </h1>
              <p style={{
              margin: '6px 0 0',
              fontSize: '14px',
              color: c.textSecondary,
              letterSpacing: '0.1px'
            }} data-id="element-201">
                {stepTitles[step].subtitle}
              </p>
            </div>
          </div>

          {/* Scrollable form body */}
          <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '48px 56px'
        }} data-id="element-202">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%'
          }} data-id="element-203">
              {step === 1 && <StepBorrower form={form} set={setField} data-id="element-204" />}
              {step === 2 && <StepProperty form={form} set={setField} data-id="element-205" />}
              {step === 3 && <StepLoanTerms form={form} set={setField} data-id="element-206" />}
              {step === 4 && <StepIPAC form={form} set={setField} data-id="element-207" />}
              {step === 5 && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '40px'
            }} data-id="element-208">
                  <StepTransaction form={form} set={setField} data-id="element-209" />
                  <div style={{
                height: '1px',
                backgroundColor: c.border
              }} data-id="element-210" />
                  <StepLender form={form} set={setField} data-id="element-211" />
                </div>}
            </div>
          </div>

          {/* Bottom Navigation */}
          <div style={{
          padding: '18px 56px',
          borderTop: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          backgroundColor: c.sidebarBg,
          transition: 'background-color 0.2s'
        }} data-id="element-212">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }} data-id="element-213">
              <button onClick={handleBack} disabled={step === 1} style={{
              padding: '11px 24px',
              borderRadius: '10px',
              border: `1px solid ${c.border}`,
              backgroundColor: 'transparent',
              color: step === 1 ? c.textMuted : c.textSecondary,
              fontSize: '14px',
              fontWeight: 500,
              cursor: step === 1 ? 'default' : 'pointer',
              opacity: step === 1 ? 0.4 : 1,
              transition: 'all 0.15s'
            }} data-id="element-214">
                ← Back
              </button>

              <span style={{
              fontSize: '12px',
              color: c.textMuted
            }} data-id="element-215">
                {STEPS[step - 1].label}
              </span>

              {step < 5 ? <button onClick={handleNext} style={{
              padding: '11px 28px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: c.green,
              color: isDark ? '#0a0a0a' : '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.3px',
              transition: 'all 0.15s',
              boxShadow: `0 2px 12px ${c.green}40`
            }} data-id="element-216">
                  Continue →
                </button> : <button onClick={handleSubmit} style={{
              padding: '11px 32px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: c.green,
              color: isDark ? '#0a0a0a' : '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.3px',
              transition: 'all 0.2s',
              boxShadow: `0 2px 12px ${c.green}40`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }} data-id="element-217">
                  🚀 Send to Underwriting
                </button>}
            </div>
          </div>

          {submitted && <div style={{
          padding: '14px 56px',
          backgroundColor: c.greenFaint,
          borderTop: `1px solid ${c.green}33`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }} data-id="element-218">
              <span style={{
            fontSize: '16px'
          }} data-id="element-219">
                ✅
              </span>
              <div data-id="element-220">
                <p style={{
              fontSize: '12px',
              fontWeight: 700,
              color: c.green,
              margin: 0,
              letterSpacing: '0.5px'
            }} data-id="element-221">
                  SUBMISSION SUCCESSFUL
                </p>
                <p style={{
              fontSize: '12px',
              color: c.textSecondary,
              margin: '2px 0 0'
            }} data-id="element-222">
                  Loan packet handed off to submission specialist and queued for
                  underwriting.
                </p>
              </div>
            </div>}
        </main>
      </div>
    </ThemeContext.Provider>;
}